import { http } from 'src/boot/axios'
import API from 'src/api'
import utils from 'src/utils'
import { Capacitor } from '@capacitor/core'

async function normalizeImage (image) {
  if (!Capacitor.isNativePlatform()) return image

  if (typeof image === 'string' && image.startsWith('data:image')) {
    return image
  }

  if (typeof image === 'string' && image.startsWith('file://')) {
    const { loadAndCompress } =
      await import('src/services/offline-images')

    const base64 = await loadAndCompress(image, 1200)

    if (typeof base64 === 'string' && base64.startsWith('data:image')) {
      return base64
    }

    return null
  }

  return image
}

async function normalizeSprintPayload (payload) {
  if (!Capacitor.isNativePlatform()) return payload
  if (!payload || !Array.isArray(payload.tasks)) return payload

  const tasks = []

  for (const task of payload.tasks) {
    const t = { ...task }

    if (Array.isArray(t.pictures)) {
      const pics = []
      for (const img of t.pictures) {
        const normalized = await normalizeImage(img)
        if (normalized) pics.push(normalized)
      }
      t.pictures = pics
    }

    tasks.push(t)
  }

  return {
    ...payload,
    tasks
  }
}


export default {
  URL: API.URL + 'sprints/',
  GRAPHQL_URL: API.GRAPHQL_URL + 'sprints',
  GRAPHQL_URL_TEMPORARY: API.GRAPHQL_URL + 'temporary',
  GRAPHQL_URL_SPRINT_DETAILS: API.GRAPHQL_URL + 'sprintDetails',

  createMissionSprint (payload) {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const normalizedPayload = await normalizeSprintPayload(payload)

          http.post(`${this.GRAPHQL_URL}`, normalizedPayload)
            .then(res => resolve(res.data.data))
            .catch(err => reject(err))
        } catch (e) {
          reject(e)
        }
      })()
    })
  },
  getMissionSprint (payload) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/getFilteredSprints`, { params: payload })
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  getMissionSprintDetails (id) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL_SPRINT_DETAILS}/${id}`)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  getMissionSprintDetailsSupervisor (payload) {
    const params = { sprint_id: payload.sprint_id, supervisor: payload.supervisor }
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL_SPRINT_DETAILS}/supervisor`, { params })
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  getSprintTasks (payload) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/userTasks`, payload)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  deleteMissionSprint (id) {
    return new Promise((resolve, reject) => {
      http.delete(`${this.GRAPHQL_URL}/${id}`)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  checkIsSupervisor () {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/checkIsSupervisor`)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  resolveTasksReview (payload) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/resolveTasksReview`, payload)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  finishSprintReview (payload) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/finishSprintReview/${payload.sprintId}/reviewer/${payload.reviewerId}`)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  getUpdateScore (id) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/updateScore/${id}`)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  getTemporarySprint () {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL_TEMPORARY}`)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  saveTemporary (payload) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL_TEMPORARY}`, payload)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  saveTemporaryImage ({ data, type, index }) {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          let normalizedData = data

          if (type === 'sprint') {
            normalizedData = await normalizeImage(data)
          }

          http.post(
            `${this.GRAPHQL_URL_TEMPORARY}/image/${index}`,
            { data: normalizedData, type }
          )
            .then(res => resolve(res.data.data))
            .catch(err => reject(err))
        } catch (e) {
          reject(e)
        }
      })()
    })
  },
  saveTemporaryDocument ({ file, type, index }) {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', type)

      http.post(
        `${this.GRAPHQL_URL_TEMPORARY}/document/${index}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  deleteTemporary () {
    const data = {
      type: 'sprint'
    }
    return new Promise((resolve, reject) => {
      http.delete(`${this.GRAPHQL_URL_TEMPORARY}`, { data })
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  downloadSprintReport (payload) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/report/${payload}`, { responseType: 'blob' })
        .then(async res => {
          if (res.status === 202) {
            const body = JSON.parse(await res.data.text())
            resolve({ queued: true, message: body.message })
            return
          }
          if (Capacitor.isNativePlatform()) {
            utils.download(`Reporte Sprint.pdf`, res.data, 'application/pdf')
          } else {
            const { saveAs } = await import('file-saver')
            saveAs(new Blob([res.data], { type: 'application/pdf' }), 'Reporte de Cierre.pdf')
          }
          resolve({ queued: false })
        })
        .catch(err => reject(err))
    })
  },
  getSprintForClone (sprintId) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/${sprintId}/clone`)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  getDocumentUrl (path) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL_TEMPORARY}/document-url`, { path })
        .then(res => resolve(res.data.url))
        .catch(err => reject(err))
    })
  }
}
